import type { GenerateNotificationVariantsResponse } from '@pushly/models/lib/structs/pushly-ai/generate-notification-variants-response'
import type { GeneratedNotificationContent } from '@pushly/models/lib/structs/pushly-ai/generated-notification-content'
import type { NotificationGenerationPolicyError } from '@pushly/models/lib/structs/pushly-ai/notification-generation-policy-error'
import { allowed, refused } from '../../../core/intent'
import type { Intent, IntentResult } from '../../../core/intent'
import type { Load } from '../../../core/load'
import { createPreviewSlice } from '../shared/preview-slice'
import type {
    DepResult,
    PreviewInput as SharedPreviewInput,
    PreviewLoad as SharedPreviewLoad,
    SegmentChoice,
} from '../shared/preview-slice'

export type { DepResult, SegmentChoice }

/** What the generate call is asked: one variant for a page, written for a segment, always fresh. */
export interface BrandVoicePreviewRequest {
    flow: 'legacy'
    domain_id: number
    segment_id: number
    num_variations: number
    fewshot_max_days: number
    content: { landing_url: string }
    skip_cache: boolean
}

/** What the generate call answers that the preview reads. */
export type GenerateVariantsAnswer = Pick<GenerateNotificationVariantsResponse, 'variants' | 'error' | 'policy_error'>

/**
 * Everything the owner needs from outside, passed in. The owner knows nothing of services, stores or the locator,
 * so a test can hand it a dependency that fails or never answers.
 */
export interface BrandVoiceOwnerDeps {
    /** The domain this owner is for. A new domain is a new owner. */
    domainId: number
    /** The domain's segments, as plain choices. */
    fetchSegments: (domainId: number) => Promise<DepResult<SegmentChoice[]>>
    generateVariants: (
        domainId: number,
        request: BrandVoicePreviewRequest,
    ) => Promise<DepResult<GenerateVariantsAnswer>>
}

export interface PreviewInput extends SharedPreviewInput {
    /** The page to write the notification for. */
    url: string
}

/** Why nothing was generated: the page could not be read. */
export interface BrandVoicePageError {
    message: string
    referenceUrl?: string
}

/** What a preview answered, as the owner holds it. */
export interface BrandVoicePreviewOutcome {
    /** Every variant the answer held, in order. One is asked for, and a switcher can read the rest later. */
    variants: GeneratedNotificationContent[]
    pageError?: BrandVoicePageError
    /** Set when the variants are returned but could not be confirmed against the Brand Voice. */
    policyError?: NotificationGenerationPolicyError
}

export type PreviewLoad = SharedPreviewLoad<BrandVoicePreviewOutcome>

/** One rule a drawn variant missed, in the author's words. */
export interface BrandVoiceAdvisoryRule {
    tier: 'guaranteed' | 'checked'
    rule: string
    reason: string
}

/** The warning drawn under a variant that could not be confirmed against the Brand Voice. */
export interface BrandVoiceAdvisory {
    message: string
    rules: BrandVoiceAdvisoryRule[]
}

export interface BrandVoiceIntents {
    /** Asks for the notification Brand Voice would write for a page and segment. The answer is the owner's `preview`. */
    'brandVoice.preview': Intent<PreviewInput, BrandVoicePreviewOutcome>
    /** Forgets the preview, so what shows no longer claims to describe changed inputs. */
    'brandVoice.resetPreview': Intent<void, void>
}

export interface BrandVoiceOwner {
    readonly domainId: number
    /** The domain's segments. Failed is not empty: a domain with none is `ready` with an empty list. */
    readonly segments: Load<SegmentChoice[]>
    readonly preview: PreviewLoad
    loadSegments: () => Promise<void>
    /**
     * A debug and test seam on a production interface: it swaps some of the owner's dependencies, for a test or the
     * console, so a preview that never answers or a request that fails reaches the screen state through the real
     * path. Nothing in the product calls it. Returns the way back.
     */
    swapDeps: (overrides: Partial<Omit<BrandVoiceOwnerDeps, 'domainId'>>) => () => void
    readonly intents: BrandVoiceIntents
}

/** Previewing generates notification copy, so a run is one variant, never a batch nobody sees. */
const PREVIEW_VARIATIONS = 1
const FEWSHOT_MAX_DAYS = 7

/** A page someone can point the preview at: a full web address. */
export const isPreviewUrl = (url: string): boolean => {
    try {
        const parsed = new URL(url.trim())
        return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
        return false
    }
}

const POLICY_MESSAGES: Record<NonNullable<NotificationGenerationPolicyError['disposition']>, string> = {
    exhausted: 'This still misses your Brand Voice after every retry.',
    not_checked: 'This could not be checked against your Brand Voice.',
    retry_failed: 'A retry to match your Brand Voice failed, so this is the attempt before it.',
}

/**
 * The advisory for one drawn variant, worded from why the batch could not be confirmed. The rules listed are only
 * the ones that variant missed, since a violation is about one variant of the batch.
 */
export const describeAdvisory = (
    policyError: NotificationGenerationPolicyError,
    variantIndex: number,
): BrandVoiceAdvisory => ({
    message:
        (policyError.disposition && POLICY_MESSAGES[policyError.disposition]) ??
        'This could not be confirmed against your Brand Voice.',
    rules: (policyError.violations ?? [])
        .filter((violation) => violation.variant_index === variantIndex)
        .map((violation) => ({ tier: violation.tier, rule: violation.rule_label, reason: violation.reason })),
})

const toOutcome = (answer: GenerateVariantsAnswer): BrandVoicePreviewOutcome => {
    // A page that could not be read generated nothing, whatever else the answer holds.
    if (answer.error) {
        return {
            variants: [],
            pageError: {
                message: answer.error.message || 'The page could not be analyzed.',
                referenceUrl: answer.error.reference_url ?? undefined,
            },
        }
    }

    const variants = answer.variants ?? []

    return {
        variants,
        policyError: variants.length && answer.policy_error ? answer.policy_error : undefined,
    }
}

/** The owner of what the Brand Voice preview knows about one domain, and the one place it changes it. */
export function createBrandVoiceOwner(baseDeps: BrandVoiceOwnerDeps): BrandVoiceOwner {
    // The dependencies in use: the ones given, with whatever is swapped over them for now. Read when asked, so a swap
    // applies to the next request.
    let overrides: Partial<BrandVoiceOwnerDeps> = {}
    const deps = new Proxy(baseDeps, {
        get: (target, key: string) => (overrides as Record<string, unknown>)[key] ?? (target as any)[key],
    })

    const previewSlice = createPreviewSlice<PreviewInput, BrandVoicePreviewOutcome>({
        isOpen: () => !!deps.domainId,
        fetchSegments: () => deps.fetchSegments(deps.domainId),
        run: async (input) => {
            const res = await deps.generateVariants(deps.domainId, {
                flow: 'legacy',
                domain_id: deps.domainId,
                segment_id: input.segmentId,
                num_variations: PREVIEW_VARIATIONS,
                fewshot_max_days: FEWSHOT_MAX_DAYS,
                content: { landing_url: input.url.trim() },
                // Someone previewing has usually just edited their Brand Voice, so a cached answer from before the
                // edit would look like the edit did not take.
                skip_cache: true,
            })

            return res.ok && res.data ? { ok: true, data: toOutcome(res.data) } : { ok: false }
        },
    })

    // The server checks the address too; this keeps a run from being asked for one it would refuse.
    const precondition = (input: PreviewInput): IntentResult<void> =>
        isPreviewUrl(input.url) ? previewSlice.precondition(input) : refused('misconfigured')

    return {
        domainId: deps.domainId,
        get segments() {
            return previewSlice.segments
        },
        get preview() {
            return previewSlice.preview
        },
        loadSegments: previewSlice.loadSegments,
        swapDeps: (next) => {
            overrides = { ...overrides, ...next }

            // Undoing one swap leaves any other in place, and does not undo a later swap of the same dependency.
            return () => {
                const rest = { ...overrides } as Record<string, unknown>
                for (const key of Object.keys(next)) {
                    if (rest[key] === (next as Record<string, unknown>)[key]) {
                        delete rest[key]
                    }
                }
                overrides = rest as Partial<BrandVoiceOwnerDeps>
            }
        },
        intents: {
            'brandVoice.preview': {
                id: 'brandVoice.preview',
                precondition,
                perform: async (input) => {
                    const refusal = precondition(input)
                    if (!refusal.ok) {
                        return refusal
                    }

                    return previewSlice.ask(input)
                },
            },
            'brandVoice.resetPreview': {
                id: 'brandVoice.resetPreview',
                precondition: () => allowed,
                perform: previewSlice.reset,
            },
        },
    }
}
